import { Trash2, X } from 'lucide-react';
import { Button } from './ui/button';

interface BulkBarProps {
  selected: Set<string>;
  onClear: () => void;
  onDelete: () => void;
  deleting: boolean;
  entityLabel?: string;
}

/**
 * Floating action bar that appears when one or more rows are selected.
 * Sits fixed at the bottom of the viewport so it doesn't push layout.
 */
export function BulkBar({
  selected,
  onClear,
  onDelete,
  deleting,
  entityLabel = 'item',
}: BulkBarProps): JSX.Element | null {
  if (selected.size === 0) return null;

  const count = selected.size;
  const label = `${count} ${entityLabel}${count === 1 ? '' : 's'} selected`;

  return (
    <div className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-xl border border-line bg-surface px-4 py-3 shadow-popover">
      <span className="text-sm font-medium text-ink">{label}</span>
      <div className="h-4 w-px bg-line" />
      <Button
        variant="ghost"
        size="sm"
        className="text-danger hover:bg-danger/10 hover:text-danger"
        disabled={deleting}
        onClick={onDelete}
      >
        <Trash2 className="h-4 w-4" />
        {deleting ? 'Deleting…' : 'Delete'}
      </Button>
      <button
        type="button"
        onClick={onClear}
        className="rounded p-1 text-ink-subtle hover:text-ink"
        aria-label="Clear selection"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Toggle a single id in/out of a Set, returning a new Set. */
export function toggleId(set: Set<string>, id: string): Set<string> {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/** Select all / deselect all from a list of ids. */
export function toggleAll(set: Set<string>, ids: string[]): Set<string> {
  if (ids.every((id) => set.has(id))) return new Set();
  return new Set(ids);
}
